// Add Project Dialog
import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useToast } from '@/contexts/ToastContext';
import { usePermissions } from '@/hooks/usePermissions';
import { organizationsService } from '@/services/collections/organizations';
import { PLAN_LIMITS } from '@/types';

const projectSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(50, 'Name too long'),
  slug: z
    .string()
    .min(2, 'Slug must be at least 2 characters')
    .max(30, 'Slug too long')
    .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  description: z.string().max(200, 'Description too long').optional(),
  domain: z.string().url('Must be a valid URL').optional().or(z.literal('')),
});

type ProjectFormData = z.infer<typeof projectSchema>;

interface AddProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AddProjectDialog = ({ open, onClose, onSuccess }: AddProjectDialogProps) => {
  const { organization, projects, refreshProjects, refreshOrganization } = useOrganization();
  const { showSuccess, showError } = useToast();
  const { plan, can } = usePermissions();

  const [saving, setSaving] = useState(false);

  const planLimits = PLAN_LIMITS[plan];
  const projectLimit = planLimits.projects;
  const atLimit = projectLimit !== -1 && projects.length >= projectLimit;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      domain: '',
    },
  });

  // Auto-generate slug from name
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 30);
    setValue('slug', slug);
  };

  const generateApiKey = (): string => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).slice(2, 10);
    return `ss_${timestamp}_${random}`;
  };

  const onSubmit = async (data: ProjectFormData) => {
    if (!organization) return;

    setSaving(true);
    try {
      // Check if slug is unique within this organization
      const existingProject = projects.find((p) => p.slug === data.slug);
      if (existingProject) {
        showError('A project with this slug already exists');
        setSaving(false);
        return;
      }

      await organizationsService.createProject(organization.id, {
        name: data.name,
        slug: data.slug,
        description: data.description || '',
        domain: data.domain || '',
        apiKey: generateApiKey(),
        createdBy: 'user',
        widgetSettings: {
          enabled: true,
          position: 'bottom-right',
          primaryColor: organization.branding?.primaryColor || '#3b82f6',
          launcherIcon: 'chat',
          showKnowledgeBase: true,
          showFeaturePortal: true,
          showContactForm: true,
          collectEmail: true,
          collectName: false,
          requireEmail: true,
          triggerDelay: 0,
          hideOnMobile: false,
          zIndex: 9999,
        },
        features: {
          feedbackEnabled: true,
          supportTicketsEnabled: true,
          featureRequestsEnabled: true,
          surveysEnabled: false,
          knowledgeBaseEnabled: true,
          screenshotCapture: false,
          sessionRecording: false,
        },
        notifications: {
          emailOnNewTicket: true,
          emailOnNewFeatureRequest: false,
        },
      });

      // Refresh data
      await refreshProjects();
      await refreshOrganization();

      showSuccess(`Project "${data.name}" created successfully`);
      reset();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Error creating project:', error);
      showError('Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      reset();
      onClose();
    }
  };

  const canCreateProject = can('project:create');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Project</DialogTitle>
      <DialogContent>
        {atLimit ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You've reached your plan limit of {projectLimit} project{projectLimit !== 1 ? 's' : ''}.
            Upgrade to add more projects.
          </Alert>
        ) : !canCreateProject ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            You don't have permission to create projects.
          </Alert>
        ) : null}

        <Box component="form" id="add-project-form" onSubmit={handleSubmit(onSubmit)} sx={{ pt: 1 }}>
          <TextField
            {...register('name')}
            label="Project Name"
            fullWidth
            required
            disabled={atLimit || !canCreateProject}
            error={!!errors.name}
            helperText={errors.name?.message || 'A friendly name for your project'}
            placeholder="My App"
            onChange={(e) => {
              register('name').onChange(e);
              handleNameChange(e as React.ChangeEvent<HTMLInputElement>);
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            {...register('slug')}
            label="Project Slug"
            fullWidth
            required
            disabled={atLimit || !canCreateProject}
            error={!!errors.slug}
            helperText={errors.slug?.message || 'Used in URLs: /p/org-slug/project-slug'}
            placeholder="my-app"
            sx={{ mb: 2 }}
          />

          <TextField
            {...register('description')}
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            disabled={atLimit || !canCreateProject}
            error={!!errors.description}
            helperText={errors.description?.message}
            placeholder="Brief description of this project"
            sx={{ mb: 2 }}
          />

          <TextField
            {...register('domain')}
            label="Domain (optional)"
            fullWidth
            disabled={atLimit || !canCreateProject}
            error={!!errors.domain}
            helperText={errors.domain?.message || 'The domain where the widget will be embedded'}
            placeholder="https://myapp.com"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-project-form"
          variant="contained"
          disabled={saving || atLimit || !canCreateProject}
          sx={{
            minWidth: 120,
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
            },
          }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
